/**
 * ForceDeliverModal Component
 * 
 * Modal for forcing delivery confirmation
 * Allows arbitrator to manually confirm delivery of a disputed item
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningIcon from '@mui/icons-material/Warning';

interface ForceDeliverModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { reason: string; deliveryConfirmed: boolean }) => void;
  disputeTitle: string;
  loading: boolean;
  error: string | null;
}

const ForceDeliverModal: React.FC<ForceDeliverModalProps> = ({
  open,
  onClose,
  onConfirm,
  disputeTitle,
  loading,
  error
}) => {
  const [reason, setReason] = useState<string>('');
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const handleSubmit = () => {
    if (!confirmed || !reason.trim()) return;
    onConfirm({ reason, deliveryConfirmed: true });
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setConfirmed(false);
      onClose();
    }
  };

  const isValid = confirmed && reason.trim().length >= 10;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'success.50'
      }}>
        <LocalShippingIcon color="success" />
        <Typography variant="h6">تأیید تحویل اجباری</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            توجه: این عمل برگشت‌ناپذیر است
          </Typography>
          <Typography variant="body2">
            با تأیید تحویل اجباری، سیستم این آیتم را به عنوان تحویل داده شده ثبت می‌کند
            و ممکن است بر روی موجودی فروشنده تأثیر بگذارد.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          اختلاف: <strong>{disputeTitle}</strong>
        </Typography>

        {/* Reason TextField */}
        <TextField
          fullWidth
          label="دلیل تأیید تحویل اجباری *"
          multiline
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="دلیل تأیید تحویل اجباری را با جزئیات وارد کنید..."
          disabled={loading}
          helperText={`حداقل 10 کاراکتر (${reason.length}/10)`}
          error={reason.length > 0 && reason.length < 10}
          sx={{ mb: 3 }}
        />

        {/* Confirmation Checkbox */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
                color="success"
              />
            }
            label={
              <Typography variant="body2">
                تأیید می‌کنم که این آیتم تحویل داده شده و دلایل کافی برای این تصمیم وجود دارد.
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          انصراف
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <LocalShippingIcon />}
        >
          {loading ? 'در حال ثبت...' : 'تأیید تحویل'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceDeliverModal;

